<template>
  <div class="treasury-container">
    <!-- Page Header -->
    <div class="page-header">
      <h1>Treasury</h1>
      <div class="header-actions">
        <button
          v-if="canManageTreasury"
          class="btn btn-primary"
          @click="showAddFunds = true"
        >
          💳 Add Funds
        </button>
      </div>
    </div>

    <!-- Loading state for overview -->
    <div v-if="loadingOverview" class="loading-container">
      <div class="spinner"></div>
      <p>Loading treasury overview...</p>
    </div>

    <div v-else>
      <!-- Metrics row -->
      <div class="metrics-grid">
        <div class="metric-card">
          <div class="card-header">
            <div class="card-icon">🏦</div>
            <div class="card-info">
              <h3>Total Treasury ({{ treasuryCurrencyCode }})</h3>
              <p class="metric-value">{{ formatAmount(displayedTreasuryTotal) }}</p>
            </div>
          </div>
        </div>

        <div class="metric-card">
          <div class="card-header">
            <div class="card-icon">📌</div>
            <div class="card-info">
              <h3>Allocated to Workstreams</h3>
              <p class="metric-value">{{ formatAmount(displayedAllocated) }}</p>
            </div>
          </div>
        </div>

        <div class="metric-card">
          <div class="card-header">
            <div class="card-icon">💼</div>
            <div class="card-info">
              <h3>Unallocated</h3>
              <p class="metric-value">{{ formatAmount(displayedUnallocated) }}</p>
            </div>
          </div>
        </div>

        <div class="metric-card">
          <div class="card-header">
            <div class="card-icon">💸</div>
            <div class="card-info">
              <h3>Spent (Period)</h3>
              <p class="metric-value">{{ formatAmount(displayedSpentPeriod) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Main card with internal tabs -->
      <div class="treasury-main-card">
        <!-- Top tabs -->
        <div class="treasury-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            :class="['treasury-tab', { active: activeTab === tab.id }]"
            @click="setActiveTab(tab.id)"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Tab content -->
        <div class="treasury-tab-content">
          <!-- OVERVIEW TAB -->
          <div v-if="activeTab === 'overview'">
            
            <div class="tab-header-row" style="margin-top: 1rem;">
              <div class="tab-title">Allocation by Workstream</div>
            </div>

            <div v-if="loadingAllocations" class="loading-container small">
              <div class="spinner"></div>
              <p>Loading active allocations...</p>
            </div>
            <div v-else-if="allocationsActive.length === 0" class="empty-state">
              <p>No active allocations found.</p>
            </div>
            <table v-else class="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Workstream</th>
                  <th>Asset</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Period</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in allocationsActive" :key="b.id">
                  <td>{{ b.title }}</td>
                  <td>{{ b.workstream.name }}</td>
                  <td>{{ b.asset.code }}</td>
                  <td>{{ formatAmount(b.total_amount) }}</td>
                  <td>
                    <span class="status-pill" :class="b.status.toLowerCase()">
                      {{ formatStatus(b.status) }}
                    </span>
                  </td>
                  <td>
                    <span v-if="b.start_date || b.end_date">
                      {{ formatDate(b.start_date) }} – {{ formatDate(b.end_date) }}
                    </span>
                    <span v-else>—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- BUDGETS TAB -->
          <div v-else-if="activeTab === 'budgets'">
            <div class="tab-header-row">
              <div class="tab-title">Budgets</div>
              <div class="tab-actions">
                <button
                  v-if="canManageBudgets"
                  class="btn btn-primary btn-sm"
                  @click="openCreateBudget"
                >
                  ➕ New Budget
                </button>
              </div>
            </div>

            <div v-if="loadingBudgets" class="loading-container small">
              <div class="spinner"></div>
              <p>Loading budgets...</p>
            </div>
            <div v-else-if="budgets.length === 0" class="empty-state">
              <p>No budgets yet. Create one to allocate funds to workstreams.</p>
            </div>
            <table v-else class="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Workstream</th>
                  <th>Asset</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Period</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in budgets" :key="b.id">
                  <td>{{ b.title }}</td>
                  <td>{{ b.workstream.name }}</td>
                  <td>{{ b.asset.code }}</td>
                  <td>{{ formatAmount(b.total_amount) }}</td>
                  <td>
                    <span class="status-pill" :class="b.status.toLowerCase()">
                      {{ formatStatus(b.status) }}
                    </span>
                  </td>
                  <td>
                    <span v-if="b.start_date || b.end_date">
                      {{ formatDate(b.start_date) }} – {{ formatDate(b.end_date) }}
                    </span>
                    <span v-else>—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- SPENDING TAB -->
          <div v-else-if="activeTab === 'spending'">
            <div class="tab-header-row">
              <div class="tab-title">Spending</div>
              <div class="tab-actions">
                <button
                  v-if="canCreatePayouts"
                  class="btn btn-primary btn-sm"
                  @click="openCreatePayout"
                >
                  ➕ New Payout
                </button>
              </div>
            </div>

            <div v-if="loadingSpending" class="loading-container small">
              <div class="spinner"></div>
              <p>Loading spending...</p>
            </div>
            <div v-else-if="spending.items.length === 0" class="empty-state">
              <p>No payouts found for the selected period.</p>
            </div>
            <table v-else class="data-table">
              <thead>
                <tr>
                  <th>Workstream</th>
                  <th>Budget</th>
                  <th>Beneficiary</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in spending.items" :key="p.id">
                  <td>{{ p.workstream?.name || '—' }}</td>
                  <td>{{ p.budget ? p.budget.title : '—' }}</td>
                  <td>{{ p.beneficiary_name }}</td>
                  <td>{{ p.category || '—' }}</td>
                  <td>{{ formatAmount(p.amount) }} {{ p.asset.code }}</td>
                  <td>
                    <span class="status-pill" :class="p.status.toLowerCase()">
                      {{ formatStatus(p.status) }}
                    </span>
                  </td>
                  <td>{{ formatDateTime(p.executed_at || p.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- LOGS TAB -->
          <div v-else-if="activeTab === 'logs'">
            <div class="tab-header-row">
              <div class="tab-title">Transactions Log</div>
              <div class="tab-actions">
                <button class="btn btn-secondary btn-sm" @click="refreshLogs">
                  🔄 Refresh
                </button>
              </div>
            </div>

            <div v-if="loadingLogs" class="loading-container small">
              <div class="spinner"></div>
              <p>Loading logs...</p>
            </div>
            <div v-else-if="transactions.length === 0" class="empty-state">
              <p>No treasury transactions recorded yet.</p>
            </div>
            <table v-else class="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Asset</th>
                  <th>Amount</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Description</th>
                  <th>External Ref</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="t in transactions" :key="t.id">
                  <td>{{ formatDateTime(t.created_at) }}</td>
                  <td>{{ t.type }}</td>
                  <td>{{ t.asset.code }}</td>
                  <td>{{ formatAmount(t.amount) }}</td>
                  <td>{{ t.from_account_label }}</td>
                  <td>{{ t.to_account_label }}</td>
                  <td>{{ t.description || '—' }}</td>
                  <td>{{ t.external_ref || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Funds Modal -->
    <div v-if="showAddFunds" class="modal-overlay" @click="showAddFunds = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Add Funds to Treasury</h2>
          <button class="modal-close" @click="showAddFunds = false">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitAddFunds">
            <div class="form-group">
              <label>Asset</label>
              <select v-model="addFundsForm.asset_id">
                <option v-for="asset in assets" :key="asset.id" :value="asset.id">
                  {{ asset.code }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Amount</label>
              <input v-model="addFundsForm.amount" type="number" step="0.01" required />
            </div>
            <div class="form-group">
              <label>Description</label>
              <input v-model="addFundsForm.description" type="text" placeholder="Optional" />
            </div>
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" @click="showAddFunds = false">Cancel</button>
              <button type="submit" class="btn btn-primary">Add Funds</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Create Budget Modal -->
    <div v-if="showCreateBudget" class="modal-overlay" @click="closeCreateBudget">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Create Budget</h2>
          <button class="modal-close" @click="closeCreateBudget">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitCreateBudget">
            <div class="form-group">
              <label>Workstream</label>
              <select v-model="budgetForm.workstream_id" required>
                <option value="">Select workstream</option>
                <option v-for="ws in workstreams" :key="ws.id" :value="ws.id">
                  {{ ws.name }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Asset</label>
              <select v-model="budgetForm.asset_id" required>
                <option v-for="asset in assets" :key="asset.id" :value="asset.id">
                  {{ asset.code }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Title</label>
              <input
                v-model="budgetForm.title"
                type="text"
                required
                placeholder="e.g., Q1 2026 Research Budget"
              />
            </div>

            <div class="form-group">
              <label>Description</label>
              <textarea
                v-model="budgetForm.description"
                rows="3"
                placeholder="Short description of what this budget covers"
              ></textarea>
            </div>

            <div class="form-group">
              <label>Total Amount</label>
              <input
                v-model="budgetForm.total_amount"
                type="number"
                step="0.01"
                min="0"
                required
              />
              <p v-if="budgetExceedsUnallocated" class="error-text">
                Amount exceeds unallocated (available: {{ formatAmount(treasuryUnallocated) }} {{ treasuryCurrencyCode }})
              </p>
            </div>

            <div class="form-group two-col">
              <div>
                <label>Start Date</label>
                <input v-model="budgetForm.start_date" type="date" />
              </div>
              <div>
                <label>End Date</label>
                <input v-model="budgetForm.end_date" type="date" />
              </div>
            </div>

            <div class="form-actions">
              <button type="button" class="btn btn-secondary" @click="closeCreateBudget">
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="creatingBudget || !isBudgetFormValid"
              >
                {{ creatingBudget ? 'Creating...' : 'Create Budget' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Create Payout Modal -->
    <div v-if="showCreatePayout" class="modal-overlay" @click="closeCreatePayout">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Create Payout</h2>
          <button class="modal-close" @click="closeCreatePayout">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitCreatePayout">
            <div class="form-group">
              <label>Budget</label>
              <select v-model="payoutForm.budget_id" required>
                <option value="">Select budget</option>
                <option
                  v-for="b in activeBudgets"
                  :key="b.id"
                  :value="b.id"
                >
                  {{ b.title }} ({{ b.asset.code }})
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Amount</label>
              <input
                v-model="payoutForm.amount"
                type="number"
                step="0.01"
                min="0"
                required
                readonly
              />
              <small v-if="selectedBudget" style="display:block;color:#6c757d;margin-top:4px;">
                Fixed from selected budget ({{ selectedBudget.asset.code }})
              </small>
            </div>

            <div class="form-group">
              <label>Category</label>
              <input
                v-model="payoutForm.category"
                type="text"
                placeholder="e.g., contractor, salary, infra"
              />
            </div>

            <div class="form-group">
              <label>Beneficiary Name</label>
              <input
                v-model="payoutForm.beneficiary_name"
                type="text"
                required
                placeholder="Who is getting paid?"
              />
            </div>

            <div class="form-group">
              <label>Invoice / Reference</label>
              <input
                v-model="payoutForm.invoice_ref"
                type="text"
                placeholder="Optional invoice or bill reference"
              />
            </div>

            <div class="form-group">
              <label>Description</label>
              <textarea
                v-model="payoutForm.description"
                rows="3"
                placeholder="Short description of what this payout is for"
              ></textarea>
            </div>

            <div class="form-actions">
              <button type="button" class="btn btn-secondary" @click="closeCreatePayout">
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="creatingPayout || !isPayoutFormValid"
              >
                {{ creatingPayout ? 'Creating...' : 'Create Payout' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { api } from '@/services/api'

export default {
  name: 'TreasuryView',
  setup() {
    const store = useStore()

    const tabs = [
      { id: 'overview', label: 'Overview' },
      { id: 'budgets', label: 'Budgets' },
      { id: 'spending', label: 'Spending' },
      { id: 'logs', label: 'Logs' }
    ]
    const activeTab = ref('overview')

    const canManageTreasury = computed(() => store.getters['auth/isAdminOrAbove'])
    const canManageBudgets = canManageTreasury
    const canCreatePayouts = canManageTreasury

    // Overview state
    const loadingOverview = ref(true)
    const overview = ref({
      asset: { id: 'usd', code: 'USD' },
      metrics: {
        total_balance: '0',
        allocated_to_workstreams: '0',
        unallocated: '0',
        total_spent_in_period: '0'
      },
      workstreams: []
    })

    // Shared data
    const workstreams = ref([])
    const assets = ref([{ id: 'usd', code: 'USD' }])

    // Budgets state
    const loadingBudgets = ref(false)
    const budgets = ref([])
    const budgetsLoaded = ref(false)

    // Spending state
    const loadingSpending = ref(false)
    const spending = ref({ items: [] })
    const spendingLoaded = ref(false)

    // Logs state
    const loadingLogs = ref(false)
    const transactions = ref([])
    const logsLoaded = ref(false)

    // Allocations for Overview (active only)
    const loadingAllocations = ref(false)
    const allocationsActive = ref([])

    // Treasury details state (GET http://localhost:8000/api/treasury)
    const loadingTreasuryDetails = ref(false)
    const treasuryDetails = ref(null)

    // Add funds state
    const showAddFunds = ref(false)
    const addFundsForm = ref({
      asset_id: 'usd',
      amount: '',
      description: ''
    })

    // Budget modal state
    const showCreateBudget = ref(false)
    const creatingBudget = ref(false)
    const budgetForm = ref({
      workstream_id: '',
      asset_id: 'usd',
      title: '',
      description: '',
      total_amount: '',
      start_date: '',
      end_date: ''
    })

    const isBudgetFormValid = computed(() => {
      const total = Number(budgetForm.value.total_amount)
      const available = Number(treasuryUnallocated.value)
      const notExceed = Number.isFinite(available) ? total <= available : true
      return (
        !!budgetForm.value.workstream_id &&
        !!budgetForm.value.asset_id &&
        !!budgetForm.value.title &&
        total > 0 &&
        notExceed
      )
    })

    // Show red warning if requested total exceeds unallocated treasury
    const budgetExceedsUnallocated = computed(() => {
      const total = Number(budgetForm.value.total_amount)
      const available = Number(treasuryUnallocated.value)
      return Number.isFinite(total) && Number.isFinite(available) && total > available
    })

    // Payout modal state
    const showCreatePayout = ref(false)
    const creatingPayout = ref(false)
    const payoutForm = ref({
      budget_id: '',
      amount: '',
      category: '',
      beneficiary_name: '',
      invoice_ref: '',
      description: ''
    })

    const activeBudgets = computed(() => {
      return budgets.value.filter(b => String(b.status || '').toUpperCase() === 'ACTIVE')
    })

    const selectedBudget = computed(() => {
      const id = payoutForm.value.budget_id
      if (!id) return null
      return budgets.value.find(b => String(b.id) === String(id)) || null
    })

    const isPayoutFormValid = computed(() => {
      return (
        !!payoutForm.value.budget_id &&
        !!payoutForm.value.beneficiary_name &&
        Number(payoutForm.value.amount) > 0
      )
    })

    // Loaders
    const loadOverview = async () => {
      loadingOverview.value = true
      try {
        const res = await api.get('/api/treasury/overview')
        if (res.data) overview.value = res.data
      } catch (e) {
        console.error('Failed to load treasury overview', e)
      } finally {
        loadingOverview.value = false
      }
    }

    const loadWorkstreams = async () => {
      try {
        const res = await api.get('/api/workstreams')
        workstreams.value = Array.isArray(res.data)
          ? res.data
          : (Array.isArray(res.data?.workstreams) ? res.data.workstreams : [])
      } catch (e) {
        console.error('Failed to load workstreams for treasury', e)
      }
    }

    // Map allocation rows from external API to our table shape
    const mapAllocationRow = (row) => {
      const assetCode = (row?.asset?.code || row?.asset_id || row?.currency || 'USD').toString().toUpperCase()
      const assetId = (row?.asset?.id || row?.asset_id || assetCode.toLowerCase())
      const total = Number(row?.total_amount ?? row?.amount ?? 0) || 0
      const spent = Number(row?.spent_amount ?? row?.spent ?? 0) || 0
      const remaining = Number(row?.remaining_amount ?? (total - spent)) || 0
      const wsId = row?.workstream_id || row?.workstream?.id || ''
      const wsName = row?.workstream?.name || row?.workstream_name || wsId || '—'
      const status = (row?.status || 'ACTIVE')
      return {
        id: row?.id || row?._id || `${wsId}-${assetId}-${row?.title || ''}`,
        title: row?.title || 'Untitled',
        workstream: { id: wsId, name: wsName },
        asset: { id: assetId, code: assetCode },
        total_amount: total,
        spent_amount: spent,
        remaining_amount: remaining,
        status,
        start_date: row?.start_date || null,
        end_date: row?.end_date || null
      }
    }

    const loadBudgets = async () => {
      if (budgetsLoaded.value) return
      loadingBudgets.value = true
      try {
        const res = await api.get('http://localhost:8000/api/treasury/allocations')
        const raw = res?.data
        const items = Array.isArray(raw?.items) ? raw.items : (Array.isArray(raw) ? raw : [])
        budgets.value = items.map(mapAllocationRow)
        budgetsLoaded.value = true
      } catch (e) {
        console.error('Failed to load budgets', e)
      } finally {
        loadingBudgets.value = false
      }
    }

    const refreshBudgets = async () => {
      budgetsLoaded.value = false
      await loadBudgets()
    }

    // Map payout rows from external API to our table shape for Spending tab
    const mapPayoutRow = (row) => {
      const assetCode = (row?.asset?.code || row?.asset_id || row?.currency || 'USD').toString().toUpperCase()
      const amount = Number(row?.amount) || 0
      const workstreamName = row?.workstream_name || row?.workstream?.name || '—'
      const budgetTitle = row?.budget_name || row?.budget?.title || row?.budget_title || '—'
      const executedAt = row?.executed_at || row?.paid_at || null
      const createdAt = row?.created_at || row?.date || null
      return {
        id: row?.id || row?._id,
        executed_at: executedAt,
        created_at: createdAt,
        workstream: { name: workstreamName },
        budget: budgetTitle !== '—' ? { title: budgetTitle } : null,
        beneficiary_name: row?.beneficiary_name || row?.beneficiary?.name || '—',
        category: row?.category || '—',
        amount,
        asset: { code: assetCode },
        status: row?.status || 'PAID'
      }
    }

    const loadSpending = async () => {
      if (spendingLoaded.value) return
      loadingSpending.value = true
      try {
        const res = await api.get('http://localhost:8000/api/treasury/payouts')
        const raw = res?.data
        const items = Array.isArray(raw?.items) ? raw.items : (Array.isArray(raw) ? raw : [])
        spending.value = { items: items.map(mapPayoutRow) }
        spendingLoaded.value = true
      } catch (e) {
        console.error('Failed to load spending', e)
      } finally {
        loadingSpending.value = false
      }
    }

    const refreshSpending = async () => {
      spendingLoaded.value = false
      await loadSpending()
    }

    const loadLogs = async () => {
      if (logsLoaded.value) return
      loadingLogs.value = true
      try {
        const res = await api.get('/api/treasury/transactions')
        transactions.value = Array.isArray(res.data?.items)
          ? res.data.items
          : (Array.isArray(res.data) ? res.data : [])
        logsLoaded.value = true
      } catch (e) {
        console.error('Failed to load treasury logs', e)
      } finally {
        loadingLogs.value = false
      }
    }

    // Load ACTIVE allocations for Overview from external service
    const loadAllocations = async () => {
      loadingAllocations.value = true
      try {
        const res = await api.get('http://localhost:8000/api/treasury/allocations')
        const raw = res?.data
        const items = Array.isArray(raw?.items) ? raw.items : (Array.isArray(raw) ? raw : [])
        const mapped = items.map(mapAllocationRow)
        allocationsActive.value = mapped.filter(x => String(x.status || '').toUpperCase() === 'ACTIVE')
      } catch (e) {
        console.error('Failed to load allocations', e)
        allocationsActive.value = []
      } finally {
        loadingAllocations.value = false
      }
    }

    // Load treasury details from external service
    const loadTreasuryDetails = async () => {
      loadingTreasuryDetails.value = true
      try {
        // Direct host per requirement; Authorization header is added by axios interceptor
        const res = await api.get('http://localhost:8000/api/treasury')
        treasuryDetails.value = res?.data ?? null
      } catch (e) {
        console.error('Failed to load treasury details', e)
        treasuryDetails.value = null
      } finally {
        loadingTreasuryDetails.value = false
      }
    }

    const refreshLogs = async () => {
      logsLoaded.value = false
      await loadLogs()
    }

    // Actions
    const setActiveTab = (tabId) => {
      activeTab.value = tabId
    }

    const submitAddFunds = async () => {
      try {
        // Map UI form -> API payload (matches curl example)
        const currency = String(addFundsForm.value.asset_id || 'usd').toLowerCase()
        const payload = {
          currency,
          amount: Number(addFundsForm.value.amount),
          desc: addFundsForm.value.description || null
        }
        await api.post('/api/treasury', payload)
        showAddFunds.value = false
        addFundsForm.value.amount = ''
        await loadOverview()
        await loadTreasuryDetails()
        logsLoaded.value = false
        await loadLogs()
      } catch (e) {
        console.error('Failed to add funds', e)
        alert('Failed to add funds')
      }
    }

    const openCreateBudget = () => {
      showCreateBudget.value = true
      if (!workstreams.value.length) loadWorkstreams()
    }

    const closeCreateBudget = () => {
      showCreateBudget.value = false
      creatingBudget.value = false
    }

    const submitCreateBudget = async () => {
      if (!isBudgetFormValid.value) return
      creatingBudget.value = true
      try {
        const payload = {
          workstream_id: budgetForm.value.workstream_id,
          asset_id: String(budgetForm.value.asset_id || 'usd').toLowerCase(),
          title: budgetForm.value.title,
          description: budgetForm.value.description || null,
          total_amount: Number(budgetForm.value.total_amount),
          start_date: budgetForm.value.start_date || null,
          end_date: budgetForm.value.end_date || null
        }
        // Call external allocation endpoint per requirement
        await api.post('http://localhost:8000/api/treasury/allocate', payload)

        budgetForm.value = {
          workstream_id: '',
          asset_id: payload.asset_id,
          title: '',
          description: '',
          total_amount: '',
          start_date: '',
          end_date: ''
        }
        showCreateBudget.value = false
        await refreshBudgets()
        await loadOverview()
        await loadTreasuryDetails()
      } catch (e) {
        console.error('Failed to create budget', e)
        alert('Failed to create budget')
      } finally {
        creatingBudget.value = false
      }
    }

    const openCreatePayout = () => {
      showCreatePayout.value = true
      if (!budgets.value.length) loadBudgets()
    }

    const closeCreatePayout = () => {
      showCreatePayout.value = false
      creatingPayout.value = false
    }

    // When budget changes, set fixed amount from selected budget
    watch(() => payoutForm.value.budget_id, () => {
      const b = selectedBudget.value
      if (!b) { payoutForm.value.amount = ''; return }
      const amt = Number.isFinite(Number(b.remaining_amount)) ? Number(b.remaining_amount) : Number(b.total_amount) || 0
      payoutForm.value.amount = amt
    })

    const submitCreatePayout = async () => {
      if (!isPayoutFormValid.value) return
      creatingPayout.value = true
      try {
        const budget = budgets.value.find(b => String(b.id) === String(payoutForm.value.budget_id))
        const assetId = String(budget?.asset?.id || budget?.asset?.code || 'usd').toLowerCase()
        const workstreamId = budget?.workstream?.id || ''

        await api.post('http://localhost:8000/api/treasury/payout', {
          workstream_id: workstreamId,
          budget_id: payoutForm.value.budget_id,
          asset_id: assetId,
          amount: Number(payoutForm.value.amount),
          category: payoutForm.value.category || null,
          beneficiary_user_id: null,
          beneficiary_name: payoutForm.value.beneficiary_name,
          invoice_ref: payoutForm.value.invoice_ref || null,
          description: payoutForm.value.description || null
        })

        payoutForm.value = {
          budget_id: '',
          amount: '',
          category: '',
          beneficiary_name: '',
          invoice_ref: '',
          description: ''
        }

        showCreatePayout.value = false
        await refreshSpending()
        await loadOverview()
        await loadTreasuryDetails()
        logsLoaded.value = false
        await loadLogs()
      } catch (e) {
        console.error('Failed to create payout', e)
        alert('Failed to create payout')
      } finally {
        creatingPayout.value = false
      }
    }

    const approvePayout = async (payout) => {
      if (!payout?.id) return
      const ok = window.confirm('Approve this payout?')
      if (!ok) return
      try {
        await api.post(`/api/payouts/${payout.id}/approve`)
        await refreshSpending()
        await loadOverview()
        logsLoaded.value = false
        await loadLogs()
      } catch (e) {
        console.error('Failed to approve payout', e)
        alert('Failed to approve payout')
      }
    }

    const executePayout = async (payout) => {
      if (!payout?.id) return
      const ok = window.confirm('Mark this payout as executed?')
      if (!ok) return
      try {
        const externalRef = window.prompt('External payment reference (optional):', '') || null
        await api.post(`/api/payouts/${payout.id}/execute`, { external_ref: externalRef })
        await refreshSpending()
        await loadOverview()
        logsLoaded.value = false
        await loadLogs()
      } catch (e) {
        console.error('Failed to execute payout', e)
        alert('Failed to execute payout')
      }
    }

    // Helpers
    const formatAmount = (val) => {
      const num = Number(val || 0)
      return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }

    // Pretty-print JSON for details panel
    const formattedTreasuryDetails = computed(() => {
      try {
        return treasuryDetails.value ? JSON.stringify(treasuryDetails.value, null, 2) : ''
      } catch (_) {
        return String(treasuryDetails.value)
      }
    })

    // Derive rows for the Overview table from active allocations
    const allocationRows = computed(() => {
      const items = allocationsActive.value || []
      const totalAlloc = items.reduce((s, x) => s + (Number(x.total_amount) || 0), 0)
      const totalSpent = items.reduce((s, x) => s + (Number(x.spent_amount) || 0), 0)
      return items.map(x => {
        const allocated = Number(x.total_amount) || 0
        const spent = Number(x.spent_amount) || 0
        const remaining = Number.isFinite(Number(x.remaining_amount)) ? Number(x.remaining_amount) : (allocated - spent)
        const allocPct = totalAlloc > 0 ? allocated / totalAlloc : 0
        const spendPct = totalSpent > 0 ? spent / totalSpent : 0
        return {
          id: x.id,
          name: x.workstream?.name || '—',
          allocated,
          spent,
          remaining,
          allocation_percent_of_total: allocPct,
          spend_percent_of_total: spendPct
        }
      })
    })

    // Resolve currency code and total amount for the top metric from treasuryDetails
    const treasuryCurrencyCode = computed(() => {
      const d = treasuryDetails.value
      const preferred = (overview.value?.asset?.code || 'USD').toString().toUpperCase()
      if (Array.isArray(d) && d.length) {
        const match = d.find(x => (x?.currency || x?.asset?.code || x?.asset_code)?.toString().toUpperCase() === preferred)
        const code = match?.currency || match?.asset?.code || match?.asset_code || d[0]?.currency || d[0]?.asset?.code || d[0]?.asset_code || preferred
        return String(code).toUpperCase()
      }
      const code = d?.currency || d?.asset?.code || d?.asset_code || preferred
      return String(code).toUpperCase()
    })

    const treasuryTotalAmount = computed(() => {
      const d = treasuryDetails.value
      // If API returned a list, sum amounts for the selected currency
      if (Array.isArray(d) && d.length) {
        const code = treasuryCurrencyCode.value
        let sum = 0
        let matched = false
        for (const row of d) {
          const rowCode = (row?.currency || row?.asset?.code || row?.asset_code || '').toString().toUpperCase()
          if (rowCode === code) {
            const n = Number(row?.amount)
            if (Number.isFinite(n)) {
              sum += n
              matched = true
            }
          }
        }
        if (matched) return sum
        // Fallback: sum all numeric 'amount' fields
        for (const row of d) {
          const n = Number(row?.amount)
          if (Number.isFinite(n)) sum += n
        }
        return sum
      }
      // Object shape fallback using common field names
      const candidates = [
        d?.amount,
        d?.total,
        d?.balance,
        d?.total_balance,
        d?.metrics?.total_balance,
        overview.value?.metrics?.total_balance
      ]
      for (const v of candidates) {
        const n = Number(v)
        if (Number.isFinite(n)) return n
      }
      return 0
    })

    // Allocated and Unallocated derived from treasury details
    const treasuryAllocated = computed(() => {
      const d = treasuryDetails.value
      if (Array.isArray(d) && d.length) {
        const code = treasuryCurrencyCode.value
        let sum = 0
        let matched = false
        for (const row of d) {
          const rowCode = (row?.currency || row?.asset?.code || row?.asset_code || '').toString().toUpperCase()
          if (rowCode === code) {
            const v = row?.allocated_to_workstream ?? row?.allocated_to_workstreams ?? row?.allocated ?? row?.metrics?.allocated_to_workstreams
            const n = Number(v)
            if (Number.isFinite(n)) { sum += n; matched = true }
          }
        }
        if (matched) return sum
        for (const row of d) {
          const n = Number(row?.allocated_to_workstream ?? row?.allocated_to_workstreams ?? row?.allocated ?? row?.metrics?.allocated_to_workstreams)
          if (Number.isFinite(n)) sum += n
        }
        if (sum) return sum
      }
      const candidates = [
        d?.allocated_to_workstream,
        d?.allocated_to_workstreams,
        d?.allocated,
        d?.metrics?.allocated_to_workstreams,
        overview.value?.metrics?.allocated_to_workstreams
      ]
      for (const v of candidates) {
        const n = Number(v)
        if (Number.isFinite(n)) return n
      }
      return 0
    })

    const treasuryUnallocated = computed(() => {
      const total = Number(treasuryTotalAmount.value)
      const alloc = Number(treasuryAllocated.value)
      const val = total - alloc
      return Number.isFinite(val) ? Math.max(0, val) : 0
    })

    // Spent derived from treasury details
    const treasurySpent = computed(() => {
      const d = treasuryDetails.value
      if (Array.isArray(d) && d.length) {
        const code = treasuryCurrencyCode.value
        let sum = 0
        let matched = false
        for (const row of d) {
          const rowCode = (row?.currency || row?.asset?.code || row?.asset_code || '').toString().toUpperCase()
          if (rowCode === code) {
            const n = Number(row?.spent ?? row?.metrics?.total_spent_in_period)
            if (Number.isFinite(n)) { sum += n; matched = true }
          }
        }
        if (matched) return sum
        for (const row of d) {
          const n = Number(row?.spent ?? row?.metrics?.total_spent_in_period)
          if (Number.isFinite(n)) sum += n
        }
        return sum
      }
      const candidates = [
        d?.spent,
        d?.metrics?.total_spent_in_period,
        overview.value?.metrics?.total_spent_in_period
      ]
      for (const v of candidates) {
        const n = Number(v)
        if (Number.isFinite(n)) return n
      }
      return 0
    })

    // Animated display values and generic count-up for metric tiles
    const displayedTreasuryTotal = ref(0)
    const displayedAllocated = ref(0)
    const displayedUnallocated = ref(0)
    const displayedSpentPeriod = ref(0)

    const _rafIds = new Map()
    const animateCountUp = (targetRef, to, duration = 1000) => {
      const key = targetRef
      if (_rafIds.has(key)) cancelAnimationFrame(_rafIds.get(key))
      const from = Math.max(0, Math.floor(Number(targetRef.value) || 0))
      const target = Math.max(0, Math.floor(Number(to) || 0))
      if (target <= from) {
        targetRef.value = target
        _rafIds.delete(key)
        return
      }
      const start = performance.now()
      let last = from
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration)
        let val = Math.floor(from + (target - from) * t)
        if (val <= last && val < target) val = last + 1 // monotonic 1,2,3,...
        last = val
        targetRef.value = val
        if (t < 1) {
          _rafIds.set(key, requestAnimationFrame(step))
        } else {
          targetRef.value = target // snap to exact
          _rafIds.delete(key)
        }
      }
      _rafIds.set(key, requestAnimationFrame(step))
    }

    // Trigger count-ups when source values change
    watch(treasuryTotalAmount, (val) => {
      animateCountUp(displayedTreasuryTotal, val, 1000)
    })
    watch(treasuryAllocated, (val) => {
      animateCountUp(displayedAllocated, val, 1000)
    })
    watch(treasuryUnallocated, (val) => {
      animateCountUp(displayedUnallocated, val, 1000)
    })
    watch(treasurySpent, (val) => {
      animateCountUp(displayedSpentPeriod, val, 1000)
    })

    const formatStatus = (status) => {
      if (!status) return '—'
      return String(status)
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase())
    }

    const formatDate = (d) => {
      if (!d) return '—'
      const date = new Date(d)
      if (isNaN(date.getTime())) return '—'
      return date.toLocaleDateString()
    }

    const formatDateTime = (d) => {
      if (!d) return '—'
      const date = new Date(d)
      if (isNaN(date.getTime())) return '—'
      return date.toLocaleString()
    }

    // Re-load tab-specific data when tab changes
    watch(activeTab, (tab) => {
      if (tab === 'overview') loadAllocations()
      if (tab === 'budgets') loadBudgets()
      if (tab === 'spending') loadSpending()
      if (tab === 'logs') loadLogs()
    })

    onMounted(() => {
      loadOverview()
      loadTreasuryDetails()
      loadAllocations()
    })

    onUnmounted(() => {
      for (const id of _rafIds.values()) cancelAnimationFrame(id)
      _rafIds.clear()
    })

    return {
      tabs,
      activeTab,
      setActiveTab,
      canManageTreasury,
      canManageBudgets,
      canCreatePayouts,
      loadingOverview,
      overview,
      workstreams,
      assets,
      loadingBudgets,
      budgets,
      showAddFunds,
      addFundsForm,
      submitAddFunds,
      showCreateBudget,
      budgetForm,
      isBudgetFormValid,
      budgetExceedsUnallocated,
      creatingBudget,
      openCreateBudget,
      closeCreateBudget,
      submitCreateBudget,
      loadingSpending,
      spending,
      showCreatePayout,
      payoutForm,
      activeBudgets,
      selectedBudget,
      isPayoutFormValid,
      creatingPayout,
      openCreatePayout,
      closeCreatePayout,
      submitCreatePayout,
      approvePayout,
      executePayout,
      loadingLogs,
      transactions,
      refreshLogs,
      // New treasury details
      loadingTreasuryDetails,
      treasuryDetails,
      loadTreasuryDetails,
      formattedTreasuryDetails,
      treasuryCurrencyCode,
      treasuryTotalAmount,
      displayedTreasuryTotal,
      treasuryAllocated,
      treasuryUnallocated,
      displayedAllocated,
      displayedUnallocated,
      displayedSpentPeriod,
      // Active allocations for Overview
      loadingAllocations,
      allocationsActive,
      allocationRows,
      loadAllocations,
      // Formatters
      formatAmount,
      formatStatus,
      formatDate,
      formatDateTime
    }
  }
}
</script>

<style scoped>
.treasury-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
}

.page-header h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-secondary {
  background: white;
  color: #667eea;
  border: 1px solid #667eea;
}

.btn-sm {
  padding: 0.35rem 0.8rem;
  font-size: 0.8rem;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.metric-card {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-icon {
  font-size: 1.6rem;
}

.card-info h3 {
  margin: 0;
  font-size: 0.9rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value {
  margin: 0.2rem 0 0;
  font-size: 1.6rem;
  font-weight: 700;
  color: #2c3e50;
}

.treasury-main-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.treasury-tabs {
  display: flex;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.treasury-tab {
  flex: 0 0 auto;
  padding: 0.75rem 1.25rem;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  color: #6c757d;
}

.treasury-tab.active {
  color: #2c3e50;
  border-bottom: 2px solid #667eea;
  background: white;
}

.treasury-tab-content {
  padding: 1.25rem 1.5rem 1.5rem;
}

.tab-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.tab-title {
  font-weight: 600;
  color: #2c3e50;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.data-table thead {
  background: #f8f9fa;
}

.data-table th,
.data-table td {
  padding: 0.6rem 0.5rem;
  border-bottom: 1px solid #f1f3f5;
  text-align: left;
  white-space: nowrap;
}

.data-table th:first-child,
.data-table td:first-child {
  padding-left: 0;
}

.data-table th:last-child,
.data-table td:last-child {
  padding-right: 0;
}

.ws-name-cell .ws-name {
  font-weight: 600;
}

.status-pill {
  display: inline-flex;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status-pill.paid {
  background: #d4edda; /* green */
  color: #155724;
}

.status-pill.active {
  background: #ffe8cc; /* orange */
  color: #d9480f;
}

.status-pill.expired {
  background: #f8d7da; /* red */
  color: #721c24;
}

.status-pill.queue {
  background: #cce5ff; /* blue */
  color: #004085;
}

/* Legacy mappings kept for compatibility */
.status-pill.pending_approval,
.status-pill.pending {
  background: #fff3cd;
  color: #856404;
}

.status-pill.closed,
.status-pill.rejected {
  background: #f8d7da;
  color: #721c24;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem;
  color: #6c757d;
}

.loading-container.small {
  padding: 1.5rem;
}

.spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  animation: spin 1s linear infinite;
  margin-bottom: 0.75rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.3rem;
  color: #2c3e50;
}

.modal-close {
  border: none;
  background: none;
  font-size: 1.4rem;
  cursor: pointer;
  color: #6c757d;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group.two-col {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 600;
  font-size: 0.9rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  font-size: 0.95rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

@media (max-width: 768px) {
  .treasury-container {
    padding: 1rem;
  }
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  .treasury-tab-content {
    padding: 1rem;
  }
.json-preview {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 8px;
  max-height: 320px;
  overflow: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.85rem;
  color: #334155;
  border: 1px solid #e9ecef;
}
.error-text {
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}
}
</style>
